import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { FC, useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { ProductWithImage } from 'shared/src/api/types'

import { LinkOrButton } from 'components/LinkOrButton'
import { makeProductDetailUrl } from 'utils/routes'
import editsBackgroundImg from '../../assets/edits-background.png'

const useStyles = createUseStyles({
  background: {
    position: 'fixed',
    width: '420px',
    height: '100vh',
    transform: 'translateY(-70px)',
    backgroundImage: `url(${editsBackgroundImg})`,
    backgroundSize: 'cover',
    zIndex: 0,
  },
  editContainer: {
    width: '100%',
    position: 'relative',
    '--img-base-viewport-w': 420,
    '--img-base-w': 390,
    '--img-base-ratio':
      'calc( var(--img-base-viewport-w) / var(--img-base-w) )',
    '--img1-top': '24px',
    '--img1-left': '24px',
    '--img1-w': 'calc(144px * var(--img-base-ratio))',
    '--img1-h': 'calc(188px * var(--img-base-ratio))',
    '--img2-top': '48px',
    '--img2-right': '24px',
    '--img2-w': 'calc(138px * var(--img-base-ratio))',
    '--img2-h': 'calc(181px * var(--img-base-ratio))',
    '--img3-top': '0px',
    '--img3-left': 'calc(var(--img1-left) + var(--img1-w) - 56px)',
    '--img3-w': 'calc(123px * var(--img-base-ratio))',
    '--img3-h': 'calc(161px * var(--img-base-ratio))',
    '--img4-top': 'calc(var(--img3-top) + var(--img3-h) - 24px)',
    '--img4-left': '28px',
    '--img4-w': 'calc(132px * var(--img-base-ratio))',
    '--img4-h': 'calc(198px * var(--img-base-ratio))',
    '--img5-top': 'calc(var(--img3-top) + var(--img3-h) - 48px)',
    '--img5-right': '32px',
    '--img5-w': 'calc(156px * var(--img-base-ratio))',
    '--img5-h': 'calc(186px * var(--img-base-ratio))',
    '--img6-top': 'calc(var(--img4-top) + var(--img4-h) - 48px)',
    '--img6-left': 'calc(var(--img4-left) + var(--img4-w) - 40px)',
    '--img6-w': 'calc(110px * var(--img-base-ratio))',
    '--img6-h': 'calc(146px * var(--img-base-ratio))',
    '--img7-top': 'calc(var(--img5-top) + var(--img5-h) - 8px)',
    '--img7-right': '16px',
    '--img7-w': 'calc(136px * var(--img-base-ratio))',
    '--img7-h': 'calc(180px * var(--img-base-ratio))',
    '--img8-top': 'calc(var(--img6-top) + var(--img6-h) - 32px)',
    '--img8-left': '16px',
    '--img8-w': 'calc(144px * var(--img-base-ratio))',
    '--img8-h': 'calc(192px * var(--img-base-ratio))',
    '--img9-top': 'calc(var(--img7-top) + var(--img7-h) - 16px)',
    '--img9-right': '60px',
    '--img9-w': 'calc(117px * var(--img-base-ratio))',
    '--img9-h': 'calc(161px * var(--img-base-ratio))',
  },
  imgFirstBlock: {
    position: 'relative',
    display: 'inline-block',
    width: '100%',
    height: 'calc(var(--img2-h) + var(--img2-top))',
    overflow: 'hidden',
  },
  imgRepeatBlock: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    marginTop: '-32px',
  },
  imgRepeatBlock1Height: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img3-top) + var(--img3-h))',
  },
  imgRepeatBlock2Height: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img4-top) + var(--img4-h))',
  },
  imgRepeatBlock3Height: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img5-top) + var(--img5-h))',
  },
  imgRepeatBlock4Height: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img6-top) + var(--img6-h))',
  },
  imgRepeatBlock5Height: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img7-top) + var(--img7-h))',
  },
  imgRepeatBlock6Height: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img8-top) + var(--img8-h))',
  },
  imgRepeatBlockFullHeight: {
    composes: '$imgRepeatBlock',
    height: 'calc(var(--img9-top) + var(--img9-h))',
  },
  img: {
    display: 'inline-block',
    position: 'absolute',
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '& img': {
      width: '100%',
      marginTop: '-50%',
    },
  },
  img1: {
    composes: '$img',
    top: 'var(--img1-top)',
    left: 'var(--img1-left)',
    width: 'var(--img1-w)',
    height: 'var(--img1-h)',
  },
  img2: {
    composes: '$img',
    top: 'var(--img2-top)',
    right: 'var(--img2-right)',
    width: 'var(--img2-w)',
    height: 'var(--img2-h)',
  },
  img3: {
    composes: '$img',
    top: 'var(--img3-top)',
    left: 'var(--img3-left)',
    width: 'var(--img3-w)',
    height: 'var(--img3-h)',
  },
  img4: {
    composes: '$img',
    top: 'var(--img4-top)',
    left: 'var(--img4-left)',
    width: 'var(--img4-w)',
    height: 'var(--img4-h)',
  },
  img5: {
    composes: '$img',
    top: 'var(--img5-top)',
    right: 'var(--img5-right)',
    width: 'var(--img5-w)',
    height: 'var(--img5-h)',
  },
  img6: {
    composes: '$img',
    top: 'var(--img6-top)',
    left: 'var(--img6-left)',
    width: 'var(--img6-w)',
    height: 'var(--img6-h)',
  },
  img7: {
    composes: '$img',
    top: 'var(--img7-top)',
    right: 'var(--img7-right)',
    width: 'var(--img7-w)',
    height: 'var(--img7-h)',
  },
  img8: {
    composes: '$img',
    top: 'var(--img8-top)',
    left: 'var(--img8-left)',
    width: 'var(--img8-w)',
    height: 'var(--img8-h)',
  },
  img9: {
    composes: '$img',
    top: 'var(--img9-top)',
    right: 'var(--img9-right)',
    width: 'var(--img9-w)',
    height: 'var(--img9-h)',
  },
})

interface Props {
  products: Array<ProductWithImage>
  userListId: number
  creatorUsername: string
}

const handleResize = (elem: HTMLElement | null) => {
  if (elem) {
    const elementWidth = elem.offsetWidth
    if (elem) {
      elem.style.setProperty('--img-base-viewport-w', `${elementWidth}`)
    }
  }
}

export const EditLayoutOne: FC<Props> = flowMax(
  addDisplayName('EditLayoutOne'),
  addProps(() => ({
    classes: useStyles(),
    handleResize,
  })),
  ({ products, userListId, creatorUsername, classes, handleResize }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
      const elem = containerRef.current
      const handling = () => handleResize(elem)
      window.addEventListener('resize', handling)
      handleResize(elem)

      return () => window.removeEventListener('resize', handling)
    }, [handleResize])

    const groupedData = []
    for (let i = 2; i < products.length; i += 7) {
      groupedData.push(products.slice(i, i + 7))
    }

    return (
      // () => (
      <div className={classes.editContainer} ref={containerRef}>
        <div className={classes.background} />
        <div className={classes.imgFirstBlock}>
          {products.slice(0, 2).map((product, index) => (
            <LinkOrButton
              navigateTo={makeProductDetailUrl(product.id, {
                userListId,
                creatorUsername,
              })}
              showBackButton
            >
              <div
                className={
                  index === 0
                    ? classes.img1
                    : index === 1
                    ? classes.img2
                    : undefined
                }
                style={
                  {
                    backgroundImage: `url(${product.image.thumbnail})`,
                  } as React.CSSProperties
                }
              ></div>
            </LinkOrButton>
          ))}
        </div>

        {groupedData.map((group) => (
          <div
            className={
              group.length === 1
                ? classes.imgRepeatBlock1Height
                : group.length === 2
                ? classes.imgRepeatBlock2Height
                : group.length === 3
                ? classes.imgRepeatBlock3Height
                : group.length === 4
                ? classes.imgRepeatBlock4Height
                : group.length === 5
                ? classes.imgRepeatBlock5Height
                : group.length === 6
                ? classes.imgRepeatBlock6Height
                : group.length === 7
                ? classes.imgRepeatBlockFullHeight
                : undefined
            }
          >
            {group.map((product, index) => (
              <LinkOrButton
                navigateTo={makeProductDetailUrl(product.id, {
                  userListId,
                  creatorUsername,
                })}
                showBackButton
              >
                <div
                  className={
                    index % 7 === 0
                      ? classes.img3
                      : index % 7 === 1
                      ? classes.img4
                      : index % 7 === 2
                      ? classes.img5
                      : index % 7 === 3
                      ? classes.img6
                      : index % 7 === 4
                      ? classes.img7
                      : index % 7 === 5
                      ? classes.img8
                      : index % 7 === 6
                      ? classes.img9
                      : undefined
                  }
                  style={
                    {
                      backgroundImage: `url(${product.image.thumbnail})`,
                    } as React.CSSProperties
                  }
                ></div>
              </LinkOrButton>
            ))}
          </div>
        ))}
      </div>
    )
  }
)
